<template>
  <div class="site-header-container">
    <header class="site-header">
      <nav class="site-header__container container">
        <!-- Left -->
        <div class="site-header__left">
          <button
            v-if="mobile.handle"
            class="site-header__menu icon-button"
            type="button"
            @click.prevent="handleMenuToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.menu')"
            />

            <icon-menu />
          </button>

          <a
            :href="$variable('routes.wishlist')"
            class="site-header__wishlist icon-button"
            @click="trackUtility($event, 'wishlist')"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.wishlist')"
            />

            <icon-wishlist />
          </a>

          <ul
            v-if="desktop.handle"
            class="site-header__navigation is-desktop"
            @mouseleave="closeMegaNavs"
          >
            <site-header-link
              v-for="linklist of desktop.items"
              :key="getKey(linklist)"
              :linklist="linklist"
              :column-counts="columnCounts"
            />
          </ul>
        </div>

        <!-- Logo -->
        <component
          :is="titleElement"
          class="site-header__center"
        >
          <a
            class="site-header__logo"
            :href="$variable('routes.root')"
          >
            <span
              id="site-header-title"
              class="visually-hidden"
              v-text="$variable('store.shopName')"
            />

            <brand-logo />
          </a>
        </component>

        <!-- Right -->
        <div class="site-header__right">
          <ul
            class="site-header__navigation"
            @mouseleave="closeMegaNavs"
          >
            <li>
              <button
                class="site-header__right-item icon-button"
                type="button"
                @click="handleSearchToggle"
              >
                <icon-search />

                <span
                  class="text-other-label-medium"
                  :class="{
                    'visually-hidden': isMobile,
                  }"
                  v-text="$string('general.site_header.search')"
                />
              </button>
            </li>

            <!-- Additional right side desktop link or mega nav -->
            <site-header-link
              v-if="rightMenuItem"
              :linklist="rightMenuItem"
              class="is-desktop"
              :column-counts="columnCounts"
            />

            <li class="is-desktop">
              <a
                class="site-header__right-item icon-button"
                :href="$variable('routes.wishlist')"
                @click="trackUtility($event, 'wishlist')"
              >
                <icon-wishlist />

                <span
                  class="text-other-label-medium"
                  :class="{
                    'visually-hidden': isMobile || !showRightLabels,
                  }"
                  v-text="$string('general.site_header.wishlist')"
                />
              </a>
            </li>

            <li class="is-desktop">
              <a
                class="site-header__right-item icon-button"
                :href="$variable('routes.account.url')"
                @click="trackUtility"
              >
                <icon-account />

                <span
                  class="text-other-label-medium"
                  :class="{
                    'visually-hidden': isMobile || !showRightLabels,
                  }"
                  v-text="$string('general.site_header.account')"
                />
              </a>
            </li>

            <li>
              <a
                class="site-header__right-item icon-button"
                :href="$variable('routes.cart.url')"
                @click="handleCartToggle"
              >
                <span
                  class="visually-hidden"
                  v-text="$string('general.site_header.toggle_bag')"
                />

                <span
                  class="visually-hidden"
                  v-text="
                    $string('cart.item_count', {
                      replace: {
                        count: cart.item_count,
                      },
                      pluralise: cart.item_count,
                    })
                  "
                />

                <icon-cart />

                <span
                  class="site-header__cart-badge critical-clear text-other-label-small"
                  aria-hidden="true"
                  v-text="cart.item_count"
                />

                <span
                  v-if="!isMobile && showRightLabels"
                  aria-hidden="true"
                  class="text-other-label-medium"
                  v-text="$string('general.site_header.bag')"
                />
              </a>
            </li>
          </ul>
        </div>
      </nav>
    </header>

    <!-- Search drawer -->
    <teleport to="body">
      <overlay
        key="searchDrawer"
        block-class="search-drawer-overlay"
        direction="fade"
        namespace="searchDrawer"
        :template="false"
        :unique="false"
        type="shelf"
      >
        <template #body="{ isActive }">
          <search-drawer
            v-if="getComponentVisible('search-drawer')"
            v-cloak
            :is-active="isActive"
            :banners="searchBanners"
          />
        </template>
      </overlay>
    </teleport>

    <!-- Menu drawer -->
    <teleport
      v-if="mobile.handle"
      to="body"
    >
      <overlay
        key="menuDrawer"
        block-class="menu-drawer-overlay"
        direction="right"
        namespace="menuDrawer"
        type="drawer"
        :show-close="false"
      >
        <template #body="overlay">
          <menu-drawer
            v-if="getComponentVisible('menu-drawer')"
            v-cloak
            :handle="mobile.handle"
            :item-count="mobile.itemCount"
            :overlay="overlay"
            :footer-links="footerLinks"
            :menu-images="mobile.menuImages.filter(Boolean)"
            :settings="mobile.settings"
          />

          <div
            v-if="!getComponentActive('menu-drawer')"
            class="navigation__menu-drawer"
          >
            <div
              v-for="(_, index) of [...Array(mobile.itemCount)]"
              :key="`menu-drawer-loading-${index}`"
              class="navigation__menu-drawer-link"
            >
              <span class="navigation__menu-drawer-loading" />
            </div>
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>
/**
 * Vue: Site header (site-header)
 * -----------------------------------------------------------------------------
 * Site header central banner containing logo, icon buttons, and menu drawer.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {Number} columnCounts.tier2 - Max number of tier 2 items in a column
 * @param {Number} columnCounts.tier3 - Max number of tier 3 items in a column
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Object} [desktopRight] - Right desktop linklist handle and items.
 * Only first item will be used.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {String} titleElement - HTML element of site logo based on page.
 * @param {Object} footerLinks - Mobile menu footer links.
 * @param {Boolean} showRightLabels - Show wishlist, account, and bag labels?
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import Overlay from '~global/overlay/overlay'
import SiteHeaderLink from '~global/site-header-group/site-header/site-header-link'

import IconAccount from '~icons/general/account.svg'
import BrandLogo from '~icons/general/brand-logo.svg'
import IconCart from '~icons/general/cart.svg'
import IconMenu from '~icons/general/menu.svg'
import IconSearch from '~icons/general/search.svg'
import IconWishlist from '~icons/general/wishlist.svg'

import { track, trackNavigationClick, trackWishlistLink } from '~/helpers/datalayer-actions'

export default {
  name: 'SiteHeader',

  components: {
    BrandLogo,
    IconAccount,
    IconCart,
    IconMenu,
    IconSearch,
    IconWishlist,
    MenuDrawer: defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.menu-drawer' */
          '~async/menu-drawer/menu-drawer'
        ),
    }),
    Overlay,
    SearchDrawer: defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.search-drawer' */
          '~async/search-drawer/search-drawer'
        ),
    }),
    SiteHeaderLink,
  },

  props: {
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    columnCounts: {
      type: Object,
      default: () => ({
        tier2: 5,
        tier3: 6,
      }),
    },
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    desktopRight: {
      type: Object,
      default: () => ({
        handle: '',
        items: [],
      }),
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        itemCount: 5,
        storeLocator: '',
        menuImages: [],
      }),
    },
    titleElement: {
      type: String,
      default: 'h1',
      validator: (value) => {
        return ['h1', 'span'].includes(value)
      },
    },
    footerLinks: {
      type: Object,
      default: () => ({
        optionalLinkText: '',
        optionalLinkUrl: '',
        contactLinks: [],
      }),
    },
    showRightLabels: {
      type: Boolean,
      default: true,
    },
    searchBanners: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isSearchPage: this.$variable('page.type') === 'search',
      rightMenuItem: this.desktopRight.items[0],
    }
  },

  computed: {
    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {
    this.handleSearchQuery()
  },

  methods: {
    trackWishlistLink,

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Close all mega navs
     */
    closeMegaNavs() {
      cnvs.EventBus.emit('mega-nav:visible', '')
    },

    /**
     * Generate a key for linklist loop
     * @param linklist
     * @returns {string}
     */
    getKey(linklist) {
      const { title, handle, items = [] } = linklist
      return `${title}-${handle}-${items.map((item) => item.title).join('-')}`
    },

    /**
     * Handle menu toggle.
     */
    handleMenuToggle() {
      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (this.$variable('page.type') === 'cart' || !this.getComponentExists('cart-drawer')) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })

      this.trackUtility(event)
    },

    /**
     * Handle search toggle.
     * @param {Object} [event] - Click event.
     */
    handleSearchToggle(event) {
      event?.preventDefault()
      const searchBar = document.getElementById('search-drawer-bar')

      this.closeMegaNavs()

      if (searchBar && this.isSearchPage) {
        searchBar.focus()
        return
      }

      if (event) {
        track('search_click')
        track({
          event: 'nav_menu_click',
          label: 'Search',
          jh_source: 'Utility Nav',
        })
      }

      this.toggleOverlay({
        component: 'search-drawer',
        ignoreDismissed: true,
        namespace: 'searchDrawer',
      })
    },

    /**
     * Open search drawer if there is a query in the parameter
     */
    handleSearchQuery() {
      const params = new URLSearchParams(window.location.search)
      const query = params.get('q')

      if (!query || this.isSearchPage) {
        return
      }

      setTimeout(() => {
        this.handleSearchToggle()
      }, this.$timing('slow'))
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      if (!this.isMobile || !this.getComponentExists('menu-drawer')) {
        return
      }

      this.openOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.closeOverlay('menuDrawer')
    },

    trackUtility(event, source) {
      if (source === 'wishlist') {
        trackWishlistLink(this.isMobile ? 'mobile' : 'desktop')
      }
      trackNavigationClick('nav_menu_click', event, { jh_source: 'Utility Nav' })
    },
  },
}
</script>
